import { useState } from "react";
import { ChevronDown, Footprints } from "lucide-react";
import { reachedIndex } from "@/lib/travelPaths";
import { quoteSnippet } from "@/lib/quoteLinks";
import { evDate } from "@/lib/eventDate";

const Stop = ({ s, i, state, color, onJump, id, evQuotes }) => (
  <button onClick={() => onJump(s)} data-testid={`journey-stop-${id}-${i}`}
    className={`relative flex w-full gap-2.5 rounded-sm px-2 py-1.5 text-left transition-colors hover:bg-[#1A202C] ${state === "now" ? "bg-[#1A202C]" : ""}`}>
    <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 border-[#0B0D10] font-mono-lf text-[10px] font-bold text-[#0B0D10]"
      style={{ background: color, opacity: state === "ahead" ? 0.4 : 1, boxShadow: state === "now" ? `0 0 0 2px ${color}` : "none" }}>{i + 1}</span>
    <span className={`min-w-0 flex-1 ${state === "ahead" ? "opacity-55" : ""}`}>
      <span className="block truncate text-sm text-[#F3EBDD]">{s.pin.entity?.name || s.pin.label || "Unnamed place"}</span>
      {s.events.map((e) => (
        <span key={e.id} className="block">
          <span className="block truncate text-[11px] text-[#A8A090]"><span className="font-mono-lf text-[#F59E0B]/80">{evDate(e)}</span> · {e.title}</span>
          {(evQuotes[e.id] || []).map((q) => (
            <span key={q.id} className="mt-0.5 block border-l border-[#D97706]/60 pl-2 font-display text-[13px] italic leading-snug text-[#E8DFD1] line-clamp-3" data-testid={`journey-quote-${q.id}`}>{quoteSnippet(q)}</span>
          ))}
        </span>
      ))}
    </span>
    {state === "now" && <span className="shrink-0 self-center text-[10px] uppercase tracking-wider text-[#F59E0B]" data-testid={`journey-now-${id}`}>here</span>}
  </button>
);

export const JourneyPanel = ({ paths, t, onJump, evQuotes = {} }) => {
  const [open, setOpen] = useState(() => window.innerWidth >= 640);
  return (
    <div className="absolute left-3 right-3 top-20 z-30 flex max-h-[calc(100%-13rem)] flex-col rounded-md border border-[#2D3442] bg-[#13171F]/90 backdrop-blur-md sm:left-auto sm:right-4 sm:w-72" data-testid="journey-panel">
      <button onClick={() => setOpen(!open)} className="flex items-center gap-2 px-3 py-2.5 text-left" data-testid="journey-panel-toggle">
        <Footprints className="h-4 w-4 text-[#F59E0B]" />
        <span className="lf-label flex-1">Journeys</span>
        <ChevronDown className={`h-4 w-4 text-[#A8A090] transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="min-h-0 space-y-3 overflow-y-auto px-2 pb-3 lf-scroll">
          {paths.map((p) => {
            const idx = reachedIndex(p.stops, t);
            return (
              <div key={p.entity.id} data-testid={`journey-${p.entity.id}`}>
                <div className="mb-1 flex items-center gap-2 px-2">
                  <span className="h-2.5 w-2.5 rounded-full" style={{ background: p.color }} />
                  <span className="truncate font-display text-lg text-[#F3EBDD]">{p.entity.name}</span>
                </div>
                {p.stops.map((s, i) => (
                  <Stop key={`${s.pin.id}-${s.t}`} s={s} i={i} id={p.entity.id} color={p.color} onJump={onJump} evQuotes={evQuotes}
                    state={i === idx ? "now" : i < idx ? "done" : "ahead"} />
                ))}
              </div>
            );
          })}
          <p className="px-2 text-[10px] text-[#A8A090]">Tap a stop to jump the time slider there.</p>
        </div>
      )}
    </div>
  );
};
